import React, { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Modal from "../../components/Modal";
import { Dropdown, Button, Select } from "antd";
import SearchBar from "../../components/SearchBar";
import {
  changeVisitStatusById,
  exportVisits,
  getClinicVisitTypes,
  getVisits,
  savePrescriptionData,
  uploadPrescriptionImage,
} from "../../apis/visit";
import { ListVisitItem } from "../../interfaces/Visit";
import AddAppointment from "./AddAppointment";
import AddFollowUpVisit from "./AddFollowUpVisit";
import { LanguageContext } from "../../providers/LanguageProvider";
import { format } from "date-fns";
import { changeStep, changeVisit } from "../../features/visit/visitSlice";
import { useAppDispatch } from "../../hooks";
import Prescription from "./Prescription";
import VisitContextMenu from "./VisitContextMenu";
import { useNotification } from "../../NotificationContext";
import EditablePrescription from "./EditablePrescription";
import { Patient } from "../../interfaces/Patient";
import { VisitMedicine } from "../../interfaces/Visit";
import CommonPagination from "../../components/CommanPagination";


interface VisitListProps {
  type?: "opd" | "ipd";
}

const VisitList: React.FC<VisitListProps> = ({ type }) => {
// const VisitList: React.FC = () => {
  const permissions = JSON.parse(
    window.localStorage.getItem("permissions") ?? ""
  );
  const user = window.localStorage.getItem("user");
  const [patientId, setPatientId] = useState<number>();
  const [parentId, setParentId] = useState<number>();
  const [clinicVisitTypeId, setClinicVisitTypeId] = useState<number>();
  const [clinicVisitTypeLayoutId, setClinicVisitTypeLayoutId] =
    useState<number>();
  const navigate = useNavigate();
  const { search } = useLocation();
  const currentDate = new Date();
  const [meta, setMeta] = useState<any>();
  const [isAppointmentModalOpen, setAppointmentModalVisibility] =
    useState<boolean>(false);
  const [isAddFollowUpVisitModalOpen, setAddFollowUpModalVisibility] =
    useState<boolean>(false);
  const [isMobileActionsVisible, setMobileActionsVisible] =
    useState<boolean>(false);

  const [isPrescriptionVisible, setIsPrescriptionVisible] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<ListVisitItem>();

  const [visits, setVisits] = useState<Array<ListVisitItem>>([]);

  const [from] = useState(currentDate.toISOString().slice(0, 10));
  const [to] = useState(currentDate.toISOString().slice(0, 10));
  const { translate: t } = useContext(LanguageContext);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [visitTypeOptions, setVisitTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [dischargingToday, setDischargingToday] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const [followUpVisitUserId, setFollowUpVisitUserId] = useState<
    number | null
  >();
  const [rescheduleVisitUserId, setRescheduleVisitUserId] = useState<
    number | any
  >();
  const [isRescheduleVisitModalOpen, setRescheduleVisitModalVisibility] =
    useState<boolean>(false);

  const [chiefComplaint, setChiefComplaint] = useState<string>("");
  const [isPrescriptionUploadModalOpen, setIsPrescriptionUploadModalOpen] =
    useState<boolean>(false);
  const [prescriptionUploadResponse, setPrescriptionUploadResponse] =
    useState<any>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isManualPrescriptionModalOpen, setIsManualPrescriptionModalOpen] =
    useState<boolean>(false);
  const [prescriptionModalMode, setPrescriptionModalMode] = useState<
    "create" | "edit"
  >("create");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchVisits({ page: 1, per_page: 10 });
    getClinicVisitTypes(type).then((res: any) => {
      const data = res?.data ?? res ?? [];
      setVisitTypeOptions(
        data.map((vt: any) => ({ value: vt.name, label: vt.name }))
      );
    }).catch(() => {});
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const dispatch = useAppDispatch();

  const handleSearch = (value: string) => {
    const updated: Record<string, any> = { ...activeFilters, page: 1 };
    if (value) updated.search = value; else delete updated.search;
    setActiveFilters(updated);
    doFetch(updated);
  };

  const handleVisitTypeFilter = (name: string | undefined) => {
    const updated: Record<string, any> = { ...activeFilters, page: 1 };
    if (name) updated.visit_type = name; else delete updated.visit_type;
    setActiveFilters(updated);
    doFetch(updated);
  };

  const handleDischargingTodayToggle = () => {
    const next = !dischargingToday;
    setDischargingToday(next);
    const updated: Record<string, any> = { ...activeFilters, page: 1 };
    if (next) updated.discharge_date = from; else delete updated.discharge_date;
    setActiveFilters(updated);
    doFetch(updated);
  };

  const handleCloseModal = () => {
    setAppointmentModalVisibility(false);
    setAddFollowUpModalVisibility(false);
    setFollowUpVisitUserId(null);
    setRescheduleVisitModalVisibility(false);
    setRescheduleVisitUserId(null);
    fetchVisits();
  };

  const doFetch = (params: Record<string, any> = {}) => {
    const finalParams: Record<string, any> = { per_page: 10, page: 1, ...params };
    if (type) finalParams.type = type;
    getVisits(finalParams).then((res: any) => {
      setVisits(res.data);
      setMeta(res.meta);
    });
  };

  const fetchVisits = (params: Record<string, any> = { page: 1, per_page: 10 }) => {
    doFetch({ ...activeFilters, ...params });
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    const updated = { ...activeFilters, page, per_page: pageSize ?? 10 };
    setActiveFilters(updated);
    doFetch(updated);
  };

  const getTodaysVisits = () => {
    if (user) {
      const loggedInUser = JSON.parse(user);
      if (!loggedInUser.isAdmin) {
        const canViewAllVisitsPermission = permissions.find(
          (el: string) => el === "can_view_all_visits"
        );
        if (canViewAllVisitsPermission) {
          fetchVisits({ date: from + "to" + to, per_page: 10 });
        } else {
          const canSeeLoggedInUserVisitsPermission = permissions.find(
            (el: string) => el === "can_see_only_logged_in_users_visits"
          );
          if (canSeeLoggedInUserVisitsPermission) {
            fetchVisits({
              date: from + "to" + to,
              per_page: 10,
              user_id: loggedInUser.id,
            });
          }
        }
      } else {
        fetchVisits({ date: from + "to" + to, per_page: 10 });
      }
    }
  };

  const handleStatusChange = (visitId: any, status: any) => {
    changeVisitStatusById({ id: visitId, visitStatus: status }).then(
      (res: any) => {
        fetchVisits();
      }
    );
  };

  const handleFollowUpVisit = (visit: ListVisitItem) => {
    setClinicVisitTypeId(visit.clinicVisitTypeId);

    if (visit?.userId) {
      setFollowUpVisitUserId(visit?.userId);
    }

    setClinicVisitTypeLayoutId(visit.clinicVisitTypeLayoutId);
    if (visit.patientId) {
      setPatientId(visit.patientId);
    }
    setParentId(visit.parentId ? visit.parentId : visit.id);
    setChiefComplaint(visit.chiefComplaint);
    setAddFollowUpModalVisibility(true);
  };

  const handleRescheduleVisit = (visit: any) => {
    setPatientId(visit.patientId);
    setSelectedVisit(visit);
    if (visit.user) {
      setRescheduleVisitUserId(visit.user.id);
    }
    setRescheduleVisitModalVisibility(true);
  };

  const downloadVisitsList = () => {
    if (search) {
      const filters = JSON.parse(
        '{"' +
          decodeURI(search.substring(1))
            .replace(/\+/g, " ")
            .replace(/"/g, '\\"')
            .replace(/&/g, '","')
            .replace(/=/g, '":"') +
          '"}'
      );
      exportVisitsList({ ...filters });
    } else {
      exportVisitsList({});
    }
  };

  const exportVisitsList = (params: any) => {
    exportVisits({ ...params, type }).then((res: any) => {
      downloadFile(res.data);
    });
  };

  const downloadFile = (data: any) => {
    const blob = new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    const today = format(new Date(), "dd_MM_yyyy_hh_mm");
    downloadLink.download = "visits-" + today + ".xlsx";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleActions = () => {
    setMobileActionsVisible(!isMobileActionsVisible);
  };

  const handleEdit = (visit: ListVisitItem) => {
    const editableVisit = { ...visit, symptoms: [], visitFiles: [] };
    dispatch(changeStep(1));
    dispatch(changeVisit(editableVisit));
    // navigate(`/visits/${visit.id}`);
    const visitType =
    visit?.clinicVisitType?.visitType?.type?.toLowerCase();
  if (visitType === "opd") {
    navigate(`/opd/visits/${visit.id}`);
  } else if (visitType === "ipd") {
    navigate(`/ipd/visits/${visit.id}`);
  } else {
    navigate(`/visits/${visit.id}`);
  }
  };

  const handlePrescriptionUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      showNotification(
        "error",
        "Please upload a valid image file (JPG, PNG, or WEBP)",
        "",
        "topRight"
      );
      return;
    }

    setIsUploading(true);
    try {
      const response = await uploadPrescriptionImage(file);

      setPrescriptionUploadResponse(response);
      setPrescriptionModalMode("edit");
      setIsPrescriptionUploadModalOpen(true);
      showNotification(
        "success",
        "Prescription image uploaded successfully",
        "",
        "topRight"
      );
    } catch (error: any) {
      showNotification(
        "error",
        error.response?.data?.message || "Failed to upload prescription image",
        "",
        "topRight"
      );
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleClosePrescriptionUploadModal = () => {
    setIsPrescriptionUploadModalOpen(false);
    setPrescriptionUploadResponse(null);
  };

  const handleOpenManualPrescription = () => {
    setPrescriptionModalMode("create");
    setIsManualPrescriptionModalOpen(true);
  };

  const handleCloseManualPrescriptionModal = () => {
    setIsManualPrescriptionModalOpen(false);
  };

  const getEmptyPrescriptionData = () => {
    const emptyPatient: Patient = {
      firstName: "",
      middleName: "",
      lastName: "",
      phone: "",
      bloodGroup: "",
      gender: "male",
      age: "",
      birthDate: "",
      address: "",
      email: "",
      comments: "",
      registrationNumber: "",
      isTobacco: false,
      isSmoking: false,
      isAlcoholic: false,
      isBloodPressure: false,
      isDiabetic: false,
      isBloodDisorder: false,
      isDrugs: false,
      drugComments: "",
      referredBy: null,
      aadharNumber: "",
      historyData: {
        familyHistory: null,
        previousSurgeries: [],
        previousDiseases: [],
        allergies: [],
        ongoingMedicines: null,
      },
    };

    return {
      patient: emptyPatient,
      medicines: [],
      symptoms: [],
      finalDiagnosis: null,
      provisionalDiagnosis: null,
      advice: "",
    };
  };

  // Transform response data to match EditablePrescription format
  const transformResponseData = (response: any) => {
    // Handle different possible response structures
    console.log("i am herer", response);
    const patientData = response.patient || response.patientData || {};
    const visitData = response.visit || response.visitData || response;
    const medicinesData = visitData.medicines || response.medicines || [];
    const symptomsData =
      visitData.symptoms || response.visit.visit.symptoms || [];
    const finalDiagnosis =
      visitData.finalDiagnosis || response.visit.visit.finalDiagnosis || null;
    const chiefComplaint =
      visitData.cheifComplaint || response.visit.visit.chiefComplaint || null;
    const provisionalDiagnosis =
      visitData.provisionalDiagnosis ||
      response.visit.visit.provisionalDiagnosis ||
      null;
    const advice = visitData.advice || response.visit.visit.advice || "";

    // Transform medicines to ensure they have the correct structure
    const transformedMedicines: Array<VisitMedicine> = medicinesData.map(
      (med: any, index: number) => {
        // Handle different medicine structures
        const medicineName =
          med.name || med.medicineName || med.instructions?.name || "";
        const instructions = med.instructions || {};

        // Helper function to convert to number, defaulting to 0
        const toNumber = (value: any): number => {
          if (value === null || value === undefined || value === "") return 0;
          const num = typeof value === "number" ? value : parseInt(value, 10);
          return isNaN(num) ? 0 : num;
        };

        return {
          id: med.id || med.medicineId || Date.now() + index,
          medicineId: med.medicineId || med.id || 0,
          name: medicineName,
          instructions: {
            name: medicineName,
            form: instructions.form || "tablet",
            morning: toNumber(
              instructions.morning || instructions.dosage?.morning
            ),
            afternoon: toNumber(
              instructions.afternoon || instructions.dosage?.afternoon
            ),
            evening: toNumber(
              instructions.evening || instructions.dosage?.evening
            ),
            night: toNumber(instructions.night || instructions.dosage?.night),
            meal: instructions.meal || "before_meal",
            note: instructions.note || instructions.notes || "",
            molecule: instructions.molecule || "",
            quantity: toNumber(instructions.quantity || med.quantity),
            dosage: instructions.dosage || "",
            frequency: instructions.frequency || "daily",
            duration: instructions.duration || "",
            durationPeriod: instructions.durationPeriod || "days",
            unit: instructions.unit || "",
          },
        };
      }
    );

    // Transform patient data
    const transformedPatient: Patient = {
      id: patientData.id,
      firstName: patientData.firstName || patientData.first_name || "",
      middleName: patientData.middleName || patientData.middle_name || "",
      lastName: patientData.lastName || patientData.last_name || "",
      phone: patientData.phone || "",
      bloodGroup: patientData.bloodGroup || patientData.blood_group || "",
      gender: patientData.gender || "male",
      age: patientData.age || "",
      birthDate: patientData.birthDate || patientData.birth_date || "",
      address: patientData.address || "",
      email: patientData.email || "",
      comments: patientData.comments || "",
      registrationNumber:
        patientData.registrationNumber || patientData.registration_number || "",
      isTobacco: patientData.isTobacco || false,
      isSmoking: patientData.isSmoking || false,
      isAlcoholic: patientData.isAlcoholic || false,
      isBloodPressure: patientData.isBloodPressure || false,
      isDiabetic: patientData.isDiabetic || false,
      isBloodDisorder: patientData.isBloodDisorder || false,
      isDrugs: patientData.isDrugs || false,
      drugComments: patientData.drugComments || "",
      referredBy: patientData.referredBy || null,
      aadharNumber: patientData.aadharNumber || "",
      historyData: patientData.historyData || {
        familyHistory: null,
        previousSurgeries: [],
        previousDiseases: [],
        allergies: [],
        ongoingMedicines: null,
      },
    };

    // Transform symptoms - handle both array and string formats
    let transformedSymptoms: Array<string> = [];
    if (Array.isArray(symptomsData)) {
      transformedSymptoms = symptomsData;
    } else if (typeof symptomsData === "string") {
      transformedSymptoms = symptomsData
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean);
    }
    console.log("return data", chiefComplaint, finalDiagnosis);
    return {
      patient: transformedPatient,
      medicines: transformedMedicines,
      symptoms: transformedSymptoms,
      finalDiagnosis,
      provisionalDiagnosis,
      chiefComplaint,
      advice,
    };
  };

  const handleSavePrescription = (data: {
    patient: Patient;
    medicines: Array<VisitMedicine>;
    symptoms: Array<string>;
    finalDiagnosis?: string | null;
    chiefComplaint?: string | null;
    advice?: string;
  }) => {
    // Transform data to array format for backend
    const payload = {
      patient: data.patient,
      medicines: data.medicines.map((medicine) => ({
        medicine_id: medicine.medicineId || null,
        name: medicine.instructions.name,
        form: medicine.instructions.form,
        meal: medicine.instructions.meal,
        morning: medicine.instructions.morning || 0,
        afternoon: medicine.instructions.afternoon || 0,
        evening: medicine.instructions.evening || 0,
        night: medicine.instructions.night || 0,
        note: medicine.instructions.note || "",
        duration: medicine.instructions.duration || "",
        duration_period: medicine.instructions.durationPeriod || "days",
        frequency: medicine.instructions.frequency || "daily",
        quantity: medicine.instructions.quantity || 0,
      })),
      symptoms: Array.isArray(data.symptoms) ? data.symptoms : [],
      final_diagnosis: data.finalDiagnosis
        ? Array.isArray(data.finalDiagnosis)
          ? data.finalDiagnosis
          : [data.finalDiagnosis]
        : [],
      chief_complaint: data.chiefComplaint || null,
      advice: data.advice || "",
    };

    // return;
    savePrescriptionData(payload)
      .then(() => {
        showNotification(
          "success",
          "Visit created successfully from prescription",
          "",
          "topRight"
        );
        handleClosePrescriptionUploadModal();
        handleCloseManualPrescriptionModal();
        fetchVisits(); // Refresh the visits list
      })
      .catch((err: any) => {
        console.error("Error creating visit:", err);
        showNotification(
          "error",
          err?.response?.data?.message || "Failed to create visit",
          "",
          "topRight"
        );
      });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex lg:hidden">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {t("visit")}
          </h1>
        </div>

        <div className="flex flex-row-reverse w-full">
          <div className="ml-2">
            <label id="listbox-label" className="sr-only">
              Actions
            </label>
            <div className="relative">
              <div className="inline-flex rounded-md divide-x shadow-sm divide-hover">
                <button
                  type="button"
                  className="inline-flex items-center p-2 rounded-md bg-primary hover:bg-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-50"
                  aria-haspopup="listbox"
                  aria-expanded="true"
                  aria-labelledby="listbox-label"
                  onClick={() => handleActions()}
                >
                  <span className="sr-only">Actions</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 20"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-4 h-4 text-white"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
                    />
                  </svg>
                </button>
              </div>

              {isMobileActionsVisible && (
                <ul
                  className="overflow-hidden absolute right-0 z-10 mt-2 w-44 bg-white rounded-md divide-y divide-gray-200 ring-1 ring-black ring-opacity-5 shadow-lg origin-top-right focus:outline-none"
                  role="listbox"
                  aria-labelledby="listbox-label"
                >
                  <li
                    className="px-4 py-2 text-sm text-gray-900 cursor-default"
                    id="listbox-option-0"
                    onClick={() => {
                      setAppointmentModalVisibility(true);
                      setMobileActionsVisible(false);
                    }}
                  >
                    {t("schedule_an_appointment")}
                  </li>
                  <li
                    className="px-4 py-2 text-sm text-gray-900 cursor-default"
                    id="listbox-option-1"
                    onClick={() => {
                      fetchVisits();
                      setMobileActionsVisible(false);
                    }}
                  >
                    {t("all_visits")}
                  </li>
                  <li
                    className="px-4 py-2 text-sm text-gray-900 cursor-default"
                    id="listbox-option-2"
                    onClick={() => {
                      getTodaysVisits();
                      setMobileActionsVisible(false);
                    }}
                  >
                    {t("todays_visits")}
                  </li>
                  <li
                    className="px-4 py-2 text-sm text-gray-900 cursor-default"
                    id="listbox-option-3"
                    onClick={() => {
                      navigate("/visit-register");
                      setMobileActionsVisible(false);
                    }}
                  >
                    {t("visit_register")}
                  </li>
                  <li
                    className="px-4 py-2 text-sm text-gray-900 cursor-default"
                    id="listbox-option-4"
                    onClick={() => {
                      downloadVisitsList();
                      setMobileActionsVisible(false);
                    }}
                  >
                    Export
                  </li>
                  <li
                    className="px-4 py-2 text-sm text-gray-900 cursor-default"
                    id="listbox-option-5"
                    onClick={() => {
                      handleOpenManualPrescription();
                      setMobileActionsVisible(false);
                    }}
                  >
                    Create Visit
                  </li>
                  <li
                    className="px-4 py-2 text-sm text-gray-900 cursor-default"
                    id="listbox-option-6"
                    onClick={() => {
                      handlePrescriptionUploadClick();
                      setMobileActionsVisible(false);
                    }}
                  >
                    {isUploading ? "Uploading..." : "Upload Prescription"}
                  </li>
                </ul>
              )}
            </div>
          </div>
          <SearchBar placeholder="Search patient..." onSearch={handleSearch}>
            <Select
              placeholder="Visit type"
              allowClear
              style={{ width: 180 }}
              options={visitTypeOptions}
              onChange={handleVisitTypeFilter}
            />
            {type === "ipd" && (
              <button
                type="button"
                onClick={handleDischargingTodayToggle}
                className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${
                  dischargingToday
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white text-orange-600 border-orange-400 hover:bg-orange-50"
                }`}
              >
                Discharging Today
              </button>
            )}
          </SearchBar>
        </div>
      </div>
      <div className="hidden lg:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {type === "ipd"
              ? "IPD Visits"
              : type === "opd"
                ? "OPD Visits"
                : t("visit")}
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            {type === "ipd"
              ? "A list of InPatient visits"
              : type === "opd"
                ? "A list of OutPatient visits"
                : t("visit_description")}
          </p>
        </div>
        <div className="justify-end mx-2 mt-4 sm:mt-0 sm:ml-16 sm:flex gap-2">
          <SearchBar placeholder="Search patient..." onSearch={handleSearch}>
            <Select
              placeholder="Visit type"
              allowClear
              style={{ width: 180 }}
              options={visitTypeOptions}
              onChange={handleVisitTypeFilter}
            />
            {type === "ipd" && (
              <button
                type="button"
                onClick={handleDischargingTodayToggle}
                className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${
                  dischargingToday
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-white text-orange-600 border-orange-400 hover:bg-orange-50"
                }`}
              >
                Discharging Today
              </button>
            )}
          </SearchBar>
          <Dropdown
            menu={{
              items: [
                {
                  key: "schedule_appointment",
                  label: (
                    <span onClick={() => setAppointmentModalVisibility(true)}>
                      {t("schedule_an_appointment")}
                    </span>
                  ),
                },
                {
                  key: "all_visits",
                  label: (
                    <span onClick={() => fetchVisits()}>{t("all_visits")}</span>
                  ),
                },
                {
                  key: "todays_visits",
                  label: (
                    <span onClick={() => getTodaysVisits()}>
                      {t("todays_visits")}
                    </span>
                  ),
                },
                {
                  key: "visit_register",
                  label: (
                    <span onClick={() => navigate("/visit-register")}>
                      {t("visit_register")}
                    </span>
                  ),
                },
                {
                  key: "export",
                  label: (
                    <span onClick={() => downloadVisitsList()}>
                      Export
                    </span>
                  ),
                },
                {
                  key: "create_visit",
                  label: (
                    <span onClick={() => handleOpenManualPrescription()}>
                      Create Visit
                    </span>
                  ),
                },
                {
                  key: "upload_prescription",
                  label: (
                    <span onClick={handlePrescriptionUploadClick}>
                      {isUploading ? "Uploading..." : "Upload Prescription"}
                    </span>
                  ),
                },
              ],
            }}
            placement="bottomRight"
          >
            <Button
              className="px-3 py-2 mr-2 text-sm font-semibold text-center text-white capitalize rounded-md shadow-sm bg-primary hover:bg-hover border-primary hover:border-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              style={{ height: "auto" }}
            >
              Actions
            </Button>
          </Dropdown>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>
      <div className="flow-root mt-8">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      {t("patient_name")}
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Visit Number
                    </th>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      {t("doctor_name")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-sm font-semibold text-gray-900"
                    >
                      {t("visit_type")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("date")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("status")}
                    </th>
                     <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("payment_status")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 capitalize"
                    >
                      {t("actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {visits && visits.length > 0 ? (
                    visits
                      .sort(
                        (a, b) =>
                          new Date(b.date).getTime() -
                          new Date(a.date).getTime()
                      )
                      .map((visit) => (
                        <tr key={visit.id}>
                          <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 capitalize whitespace-nowrap sm:pl-6">
                            {visit.patient?.firstName} {visit.patient?.lastName}{" "}
                          </td>
                          <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6">
                            <div>{(visit as any).visitNumber || (visit as any).serialNumber || visit.id}</div>
                            {(visit as any).episode?.episodeType?.name && (
                              <span className="mt-1 inline-block text-xs px-2 py-0.5 rounded bg-green-100 text-green-800 border border-green-400">
                                {(visit as any).episode.episodeType.name}
                              </span>
                            )}
                          </td>
                          <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 capitalize whitespace-nowrap sm:pl-6">
                            {visit.user?.firstName} {visit.user?.lastName}
                          </td>
                          <td className="flex flex-col items-center gap-2 px-3 py-4 text-sm whitespace-nowrap">
                            <span
                              className={`text-sm font-medium px-2.5 py-0.5 rounded capitalize
                               ${visit.clinicVisitType?.visitType?.type === "ipd"
                                  ? "bg-purple-100 text-purple-800 border border-purple-400"
                                  : visit.clinicVisitType?.visitType?.type === "opd"
                                    ? "bg-blue-100 text-blue-800 border border-blue-400"
                                    : "bg-gray-100 text-gray-800"
                                }
                              `}
                            >
                              {visit.clinicVisitType?.visitType?.name}
                            </span>


                            {visit.isInpatient && visit.roomNo && (
                              <span className="text-xs text-gray-600">
                                Room No: <strong>{visit.roomNo}</strong>
                              </span>
                            )}

                            {visit.parentId && (
                              <span className="bg-indigo-100 text-indigo-800 text-sm font-medium px-2.5 py-0.5 rounded dark:bg-indigo-200 dark:text-indigo-900 capitalize">
                                Follow Up
                              </span>
                            )}
                          </td>

                          <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                            {format(
                              new Date(visit.date),
                              "dd MMM yyyy, hh:mm a"
                            )}
                          </td>

                          <td
                            className={`py-4 pr-3 pl-4 text-sm font-medium text-gray-900 capitalize whitespace-nowrap sm:pl-0`}
                          >
                            {visit.visitStatus === "scheduled" ? (
                              <span className="bg-pink-100 text-pink-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-pink-200 border border-pink-400 dark:text-pink-900 capitalize">
                                {t("scheduled")}
                              </span>
                            ) : visit.visitStatus === "checked_in" ? (
                              <span className="bg-blue-100 text-blue-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-blue-200 border border-blue-400 dark:text-blue-900 capitalize">
                                {t("checked_in")}
                              </span>
                            ) : visit.visitStatus === "completed" ? (
                              <span className="bg-green-100 text-green-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-200 border border-green-400 dark:text-green-900 capitalize">
                                {t("completed")}
                              </span>
                            ) : visit.visitStatus === "cancelled" ? (
                              <span className="bg-red-100 text-red-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-red-200 border border-red-400 dark:text-red-900 capitalize">
                                {t("cancelled")}
                              </span>
                            ) : visit.visitStatus === "expired" ? (
                              <span className="bg-gray-100 text-gray-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-gray-200 border border-gray-400 dark:text-gray-900 capitalize">
                                {t("expire")}
                              </span>
                            ) : null}
                          </td>

                          <td
                            className={`py-4 pr-3 pl-4 text-sm font-medium text-gray-900 capitalize whitespace-nowrap sm:pl-0`}
                          >
                            {visit.invoice?.status === "partial" ? (
                              <span className="bg-pink-100 text-pink-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-pink-200 border border-pink-400 dark:text-pink-900 capitalize">
                                {t("partial")}
                              </span>
                            ) : visit.invoice?.status === "paid" ? (
                              <span className="bg-green-100 text-green-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-green-300 border border-green-400 dark:text-green-900 capitalize">
                                {t("paid")}
                              </span>
                            ) : visit.invoice?.status === "pending" ? (
                              <span className="bg-red-100 text-red-800 text-sm font-medium mr-2 px-2.5 py-0.5 rounded dark:bg-red-300 border border-red-400 dark:text-red-900 capitalize">
                                {t("pending")}
                              </span>
                            ) : "--"}
                          </td>

                          <td className="relative py-4 pr-4 pl-3 text-sm font-medium text-left capitalize whitespace-nowrap sm:pr-6">
                            <VisitContextMenu
                              visit={visit}
                              onReschedule={handleRescheduleVisit}
                              onFollowUp={handleFollowUpVisit}
                              onStatusChange={handleStatusChange}
                            />
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr className="px-4 py-2 bg-white">
                      <td
                        colSpan={8}
                        className="py-4 pr-3 pl-4 text-sm font-medium text-center text-gray-900 sm:pl-6"
                      >
                        {t("Record Not Found")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>
      {isAppointmentModalOpen && (
        <Modal
          title="Appointment"
          onClose={() => handleCloseModal()}
          modalContent={
            <AddAppointment
              patientId={null}
              closeModal={() => handleCloseModal()}
            />
          }
        />
      )}
      {isAddFollowUpVisitModalOpen && followUpVisitUserId && (
        <Modal
          title="Follow Up Visit"
          onClose={() => handleCloseModal()}
          modalContent={
            <AddFollowUpVisit
              patientId={patientId}
              parentId={parentId}
              userId={followUpVisitUserId}
              chiefComplaint={chiefComplaint}
              clinicVisitTypeId={clinicVisitTypeId}
              clinicVisitTypeLayoutId={clinicVisitTypeLayoutId}
              closeModal={() => handleCloseModal()}
            />
          }
        />
      )}
      {isRescheduleVisitModalOpen && (
        <Modal
          title="Appointment"
          onClose={() => handleCloseModal()}
          modalContent={
            <AddAppointment
              patientId={patientId}
              userId={rescheduleVisitUserId}
              existingVisitData={selectedVisit}
              closeModal={() => handleCloseModal()}
            />
          }
        />
      )}

      {isPrescriptionVisible && selectedVisit && (
        <Modal
          width="max-w-2xl"
          title="Prescription Preview"
          onClose={() => setIsPrescriptionVisible(false)}
          modalContent={
            <Prescription
              preview
              prescriptionDetails={{
                symptoms: selectedVisit.symptoms,
                medicines: selectedVisit.medicines,
                finalDiagnosis: selectedVisit.finalDiagnosis,
                provisionalDiagnosis: null,
                advice: selectedVisit.advice,
                patient: selectedVisit.patient,
                visitId: selectedVisit.id,
              }}
            />
          }
        />
      )}

      {/* Edit Prescription Modal from Upload */}
      {isPrescriptionUploadModalOpen && prescriptionUploadResponse && (
        <Modal
          width="max-w-6xl"
          title={
            prescriptionModalMode === "edit"
              ? "Edit Prescription"
              : "Create Visit"
          }
          onClose={handleClosePrescriptionUploadModal}
          modalContent={
            <EditablePrescription
              {...transformResponseData(prescriptionUploadResponse)}
              mode={prescriptionModalMode}
              onSave={handleSavePrescription}
              onCancel={handleClosePrescriptionUploadModal}
            />
          }
        />
      )}

      {/* Manual Prescription Entry Modal */}
      {isManualPrescriptionModalOpen && (
        <Modal
          width="max-w-6xl"
          title={
            prescriptionModalMode === "create"
              ? "Create Visit"
              : "Edit Prescription"
          }
          onClose={handleCloseManualPrescriptionModal}
          modalContent={
            <EditablePrescription
              {...getEmptyPrescriptionData()}
              mode={prescriptionModalMode}
              onSave={handleSavePrescription}
              onCancel={handleCloseManualPrescriptionModal}
            />
          }
        />
      )}
    </div>
  );
};

export default VisitList;
